/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Button from "@eisinc/MobiusOverrides/Button";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    headingLevel = "headingLevel",
    copy = "copy",
    buttonText = "buttonText",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    openInNewTab = "openInNewTab",
    horizontalAlignment = "horizontalAlignment",
}

interface SimpleContentProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.headingLevel]: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
        [fields.copy]: string;
        [fields.buttonText]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.openInNewTab]: boolean;
        [fields.horizontalAlignment]: string;
    };
}

const mapStateToProps = (state: ApplicationState, SimpleContentProps: SimpleContentProps) => {
    return getLink(state, SimpleContentProps.fields.buttonLink);
};

type Props = SimpleContentProps & ReturnType<typeof mapStateToProps>;

const CustomLink: any = Button;

const simpleContentTemplateWidget: React.FC<Props> = ({ url, fields }) => {
    const wrapperCss = css`
        display: flex;
        flex-direction: column;
        align-items: ${fields.horizontalAlignment};
        margin: 20px 0;

        a {
            display: inline-flex;
            align-items: center;
        }
    `;

    const headingCss = css`
        margin-bottom: 10px;
    `;

    return (
        <StyledWrapper css={wrapperCss}>
            <Typography variant={fields.headingLevel} weight="normal" css={headingCss}>
                {fields.heading}
            </Typography>
            {fields.copy && (
                <>
                    {parse(fields.copy, parserOptions)}
                </>
            )}
            {fields.buttonText && (
                <CustomLink
                    as="a"
                    href={url}
                    variant={fields.buttonVariant}
                    target={fields.openInNewTab ? "_blank" : undefined}
                    rel={fields.openInNewTab ? "noopener" : undefined}
                >
                    {fields.buttonText}
                </CustomLink>
            )}
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.heading,
            displayName: "Heading",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.headingLevel,
            displayName: "Heading Level",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "H1", value: "h1" },
                { displayName: "H2", value: "h2" },
                { displayName: "H3", value: "h3" },
                { displayName: "H4", value: "h4" },
                { displayName: "H5", value: "h5" },
                { displayName: "H6", value: "h6" },
            ],
            isRequired: true,
            defaultValue: "h3",
            fieldType: "General",
        },
        {
            name: fields.copy,
            displayName: "Copy",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.buttonText,
            displayName: "Button Label",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.buttonLink,
            displayName: "Button Link",
            editorTemplate: "CustomLinkField" as any,
            defaultValue: { value: "", type: "Page" },
            fieldType: "General",
            isRequired: true,
            validate: (link: LinkFieldValue) => {
                if (!link || !link.value) {
                    return "A value must be provided.";
                }
                return null;
            },
        },
        {
            name: fields.buttonVariant,
            displayName: "Button Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: "primary" },
                { displayName: "Secondary", value: "secondary" },
                { displayName: "Tertiary", value: "tertiary" },
            ],
            defaultValue: "primary",
            fieldType: "General",
            isRequired: true,
        },
        {
            name: fields.openInNewTab,
            displayName: "Open in New Tab",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.horizontalAlignment,
            displayName: "Horizontal Alignment",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Left", value: "flex-start" },
                { displayName: "Center", value: "center" },
                { displayName: "Right", value: "flex-end" },
            ],
            isRequired: true,
            defaultValue: "center",
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(simpleContentTemplateWidget),
    definition,
};

export default widgetModule;
